import type React from "react";
import { AsciiField, PixelLogo, ScrambleText } from "@rinci/brand/react";

export const signInPath = "#/sign-in";

const steps = [
  {
    label: "Queued",
    body: "The request returns as soon as the email is on the queue. A background worker does the sending, so a slow provider never holds up your app.",
  },
  {
    label: "Routed",
    body: "The worker tries each enabled provider in order, SMTP first by default, then Zoho and Resend, until one accepts the message.",
  },
  {
    label: "Recorded",
    body: "Every send, failure and API request is stored. Failed emails can be resent from the dashboard.",
  },
];

const features = [
  {
    title: "Change routing without a deploy",
    body: "Provider order and on/off state live in Redis. Reorder or switch off a provider from the dashboard and the next email uses it.",
  },
  {
    title: "Safe to retry",
    body: "Send an x-idempotency-key header and a repeat of the same key, recipient and subject is not queued twice. Resend calls carry a key per job as well.",
  },
  {
    title: "One body field",
    body: "Send plain text or HTML in the same body field. Hub writes the plain-text fallback for HTML emails.",
  },
  {
    title: "A key per app",
    body: "Each app gets its own Hub API key with its own scopes. Keys can be revoked or renewed from the dashboard, and the full key is shown only once.",
  },
  {
    title: "Several sender accounts",
    body: "Pick a configured SMTP account per email with the account field, or leave it out to use the default sender.",
  },
  {
    title: "Usage you can read",
    body: "Requests, failure rate, latency, and email status by provider for the last 24 hours, for your account or the whole workspace.",
  },
];

function brandDelay(ms: number) {
  return { "--brand-delay": `${ms}ms` } as React.CSSProperties;
}

function PrimaryLink({ children }: { children: string }) {
  return (
    <a
      className="pixel-type inline-flex items-center justify-center bg-paper px-6 py-3.5 text-[12px] text-ink transition-colors duration-150 hover:bg-signal active:translate-y-px"
      href={signInPath}
    >
      <ScrambleText text={children} />
    </a>
  );
}

export function Landing() {
  return (
    <div className="min-h-[100dvh] bg-ink text-paper">
      <header className="sticky top-0 z-40 border-b border-line bg-ink/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-6 px-5 sm:px-8">
          <a className="group flex items-center gap-3" href="#/" aria-label="Rinci Hub home">
            <PixelLogo label="RINCI" mark width={16} />
            <span className="pixel-type text-[12px] text-paper transition-colors group-hover:text-signal">
              Hub
            </span>
          </a>
          <nav aria-label="Main">
            <ul className="flex items-center gap-5 text-[13px] sm:gap-7">
              <li>
                <a className="text-dim transition-colors hover:text-paper" href="#how-it-works">
                  How it works
                </a>
              </li>
              <li>
                <a className="text-paper transition-colors hover:text-signal" href={signInPath}>
                  Sign in
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <main id="content">
        <section className="relative isolate flex flex-col items-center overflow-hidden border-b border-line px-5 pt-24 pb-20 text-center sm:px-8 md:pt-32 md:pb-24">
          <div className="ascii-hero absolute inset-0 -z-10">
            <AsciiField />
          </div>
          <div className="hero-copy relative flex w-full max-w-2xl flex-col items-center">
            <PixelLogo label="Rinci Hub" mark width={44} />
            <ScrambleText
              as="p"
              className="pixel-type brand-in mt-8 text-[12px] text-signal"
              delay={500}
              text="Rinci Hub"
            />
            <h1
              className="brand-in mt-5 max-w-[17ch] text-[34px] leading-[1.04] font-medium tracking-tight text-balance sm:text-[46px] md:text-[58px]"
              style={brandDelay(620)}
            >
              Email for every RINCI app. One API.
            </h1>
            <p
              className="brand-in mt-6 max-w-[50ch] text-[15px] leading-relaxed text-dim md:text-[16px]"
              style={brandDelay(740)}
            >
              Send transactional email with one request. Hub queues it, tries SMTP, Zoho and
              Resend in the order you set, and keeps a record of every send and failure.
            </p>
            <div
              className="brand-in mt-9 flex w-full max-w-md flex-col justify-center gap-3 sm:flex-row"
              style={brandDelay(860)}
            >
              <PrimaryLink>Sign in</PrimaryLink>
              <a
                className="pixel-type inline-flex items-center justify-center border border-line px-6 py-3.5 text-[12px] text-paper transition-colors duration-150 hover:border-signal hover:text-signal"
                href="#how-it-works"
              >
                How it works
              </a>
            </div>
          </div>
        </section>

        <section className="border-b border-line bg-ink-2">
          <div className="mx-auto grid w-full max-w-6xl gap-12 px-5 py-20 sm:px-8 md:grid-cols-[1fr_1.2fr] md:items-center md:py-24">
            <div className="reveal">
              <h2 className="max-w-[16ch] text-[28px] leading-tight font-medium tracking-tight md:text-[36px]">
                One request to send.
              </h2>
              <p className="mt-5 max-w-[44ch] text-[15px] leading-relaxed text-dim">
                Post to <code className="font-mono text-paper">/api/emails/send</code> with a Hub API
                key. The response comes back once the email is queued, and the dashboard shows where
                it went from there.
              </p>
            </div>
            <div className="reveal min-w-0 border border-line bg-ink font-mono text-[12.5px] leading-[1.9] sm:text-[13px]">
              <div className="flex items-center gap-1.5 border-b border-line px-4 py-2.5" aria-hidden="true">
                <span className="size-1.5 bg-line" />
                <span className="size-1.5 bg-line" />
                <span className="size-1.5 bg-signal" />
              </div>
              <pre className="overflow-x-auto px-4 py-4">
                <code>
                  <span className="text-faint">$</span>{" "}
                  <span className="text-paper">curl https://hub.rin.ci/api/emails/send \</span>
                  {"\n"}
                  <span className="text-dim">{'    -H "Authorization: Bearer $HUB_API_KEY" \\'}</span>
                  {"\n"}
                  <span className="text-dim">{'    -H "Content-Type: application/json" \\'}</span>
                  {"\n"}
                  <span className="text-dim">{'    -d \'{"to":"team@example.com","subject":"Hello","body":"Sent from Hub."}\''}</span>
                  {"\n\n"}
                  <span className="text-signal">{'{ "queued": true }'}</span>
                </code>
              </pre>
            </div>
          </div>
        </section>

        <section className="scroll-mt-14 border-b border-line" id="how-it-works">
          <div className="mx-auto w-full max-w-6xl px-5 py-20 sm:px-8 md:py-24">
            <h2 className="reveal max-w-[20ch] text-[28px] leading-tight font-medium tracking-tight md:text-[36px]">
              What happens after the request.
            </h2>
            <ol className="mt-12 grid border-t border-l border-line md:grid-cols-3">
              {steps.map((step, index) => (
                <li className="reveal border-r border-b border-line p-6" key={step.label}>
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="pixel-type text-[12px] text-paper">{step.label}</span>
                    <span className="font-mono text-[11.5px] text-faint">0{index + 1}</span>
                  </span>
                  <p className="mt-3 text-[13.5px] leading-relaxed text-dim">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-line bg-ink-2">
          <div className="mx-auto w-full max-w-6xl px-5 py-20 sm:px-8 md:py-24">
            <h2 className="reveal max-w-[18ch] text-[28px] leading-tight font-medium tracking-tight md:text-[36px]">
              Built for the emails an app has to send.
            </h2>
            <div className="mt-14 grid gap-x-12 md:grid-cols-2">
              {features.map((item) => (
                <article className="reveal border-t border-line py-7" key={item.title}>
                  <h3 className="flex items-center gap-3 text-[15px] font-medium text-paper">
                    <span aria-hidden="true" className="size-1.5 shrink-0 bg-signal" />
                    {item.title}
                  </h3>
                  <p className="mt-2 max-w-[52ch] pl-[18px] text-[14px] leading-relaxed text-dim">
                    {item.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-24 text-center sm:px-8 md:py-28">
          <div className="mx-auto w-full max-w-2xl">
            <h2 className="reveal text-[28px] leading-[1.08] font-medium tracking-tight text-balance md:text-[40px]">
              Have a Hub API key?
            </h2>
            <p className="reveal mx-auto mt-5 max-w-[44ch] text-[15px] leading-relaxed text-dim">
              Sign in with it to see your sends, usage and logs. Keys are issued by a Hub admin.
            </p>
            <div className="reveal mt-9 flex justify-center">
              <PrimaryLink>Sign in</PrimaryLink>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-5 py-8 text-[13px] text-faint sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <a className="pixel-type text-[11px] transition-colors hover:text-paper" href="https://rin.ci">
            RINCI
          </a>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            <li>
              <a className="transition-colors hover:text-paper" href={signInPath}>
                Dashboard
              </a>
            </li>
            <li>
              <span>API at hub.rin.ci</span>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}
